/**
 * Shared template for all /business/software/* pages.
 * Clean hero band + feature bullets + stats — no simulations, no illustrations.
 */
import type { LucideIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { B2BMarketingPageLayout } from '../_shared/B2BMarketingPageLayout';

export type SoftwareFeatureContent = {
  eyebrow: string;
  title: string;
  subtitle: string;
  description: string;
  /** Tailwind inline-style gradient string for hero band */
  heroBg: string;
  /** Tailwind colour class for accent text */
  accentText: string;
  stats: { value: string; label: string }[];
  sections: {
    heading: string;
    bullets: string[];
  }[];
};

function FadeUp({ children, delay = 0, className = '' }: { children: React.ReactNode; delay?: number; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.45, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function SoftwareFeaturePage({ content }: { content: SoftwareFeatureContent }) {
  return (
    <B2BMarketingPageLayout>

      {/* Hero band */}
      <section
        className="relative overflow-hidden px-4 pb-14 pt-14 sm:px-6 md:px-8 md:pb-16 md:pt-20"
        style={{ background: content.heroBg }}
      >
        <FadeUp className="relative z-10 mx-auto max-w-2xl text-center">
          <span className={`text-[11px] font-semibold uppercase tracking-[0.14em] ${content.accentText}`}>
            {content.eyebrow}
          </span>
          <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl">
            {content.title}
          </h1>
          <p className={`mt-2 text-base font-medium ${content.accentText}`}>{content.subtitle}</p>
          <p className="mt-3 text-sm leading-relaxed text-gray-500">{content.description}</p>
        </FadeUp>
      </section>

      {/* Stats strip */}
      <section className="border-b border-gray-100 bg-white/70 px-4 py-8 dark:border-zinc-800 dark:bg-zinc-900/60 sm:px-6 md:px-8">
        <FadeUp>
          <div className="mx-auto grid max-w-3xl grid-cols-2 gap-6 md:grid-cols-4">
            {content.stats.map((s) => (
              <div key={s.label} className="text-center">
                <p className={`text-2xl font-extrabold ${content.accentText}`}>{s.value}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-zinc-400">{s.label}</p>
              </div>
            ))}
          </div>
        </FadeUp>
      </section>

      {/* Feature sections */}
      <section className="px-4 py-14 sm:px-6 md:px-8 md:py-18">
        <div className="mx-auto max-w-4xl space-y-12">
          {content.sections.map((sec, i) => (
            <FadeUp key={sec.heading} delay={0.06 * i}>
              <h2 className="mb-4 text-lg font-bold text-gray-900 dark:text-white">{sec.heading}</h2>
              <ul className="grid gap-2 sm:grid-cols-2">
                {sec.bullets.map((b) => (
                  <li key={b} className="flex items-start gap-2.5 text-sm text-gray-600 dark:text-zinc-300">
                    <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${content.accentText}`} aria-hidden />
                    {b}
                  </li>
                ))}
              </ul>
            </FadeUp>
          ))}
        </div>
      </section>

    </B2BMarketingPageLayout>
  );
}
